import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  Bike,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Loader,
  Send,
  Info,
  Calculator,
  Star,
  Shield,
  Award,
  ArrowRight,
  FileText,
  Camera,
  Upload,
  AlertTriangle,
  ChevronDown,
  Building
} from 'lucide-react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

const CustomerReservationForm = ({ companyId, companyName }) => {
  // Form state
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    whatsappNumber: '',
    localNumber: '',
    licenseNumber: '',
    pickupDateTime: '',
    dropoffDateTime: '',
    pickupLocation: 'SLBR',
    dropoffLocation: 'SLBR',
    bikeModel: '',
    depositAmount: '',
    note: ''
  });

  // Model-based selection state
  const [selectedBikeModel, setSelectedBikeModel] = useState('');
  const [bikeAvailabilityMap, setBikeAvailabilityMap] = useState({});
  const [modelAvailabilityMap, setModelAvailabilityMap] = useState({});

  // System state
  const [availableBikes, setAvailableBikes] = useState([]);
  const [reservationSettings, setReservationSettings] = useState({
    rentalCalculationMethod: 'inclusive',
    minimumRentalDays: 1,
    allowSameDayRental: true,
    depositOptions: [
      { id: 1, amount: '100', currency: 'USD', symbol: '$', label: '$100', isDefault: true }
    ],
    requireCustomerEmail: true,
    requireLicenseNumber: true,
    defaultPickupLocation: 'SLBR',
    defaultDropoffLocation: 'SLBR',
    rentalAddons: [],
    documentRequirements: [],
    branding: {
      showLogo: true,
      showCompanyName: true,
      logoSize: 'medium',
      logoPosition: 'center',
      nameSize: 'medium',
      namePosition: 'center',
      nameColor: 'from-orange-400 via-red-500 to-purple-600',
      showBusinessRegNumber: true,
      businessRegNumberColor: 'text-gray-400',
      showAddress: true,
      addressColor: 'text-gray-400'
    }
  });

  // ⭐⭐⭐ NEW: Profile Data from MyAccount ⭐⭐⭐
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Company branding state - now populated from profile data
  const [companyBranding, setCompanyBranding] = useState({
    logoUrl: null,
    displayName: companyName || '',
    address: '',
    phone: '',
    email: '',
    website: '',
    businessRegNumber: ''
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [reservationNumber, setReservationNumber] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});

  // Calculation state
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [calculationDetails, setCalculationDetails] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  
  // Addon state
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [addonCosts, setAddonCosts] = useState(0);

  // Document Requirements state
  const [documentUploads, setDocumentUploads] = useState({});
  const [documentValidation, setDocumentValidation] = useState({});

  // Firebase collections
  const managedBikesCollection = useMemo(() => collection(db, 'managedBikes'), []);
  const reservationsCollection = useMemo(() => {
    if (!companyId) return null;
    return collection(db, `companies/${companyId}/reservations`);
  }, [companyId]);
  const settingsDocRef = useMemo(() => {
    if (!companyId) return null;
    return doc(db, `companies/${companyId}/settings`, 'reservations');
  }, [companyId]);

  // ╔═══════════════════════════════════════════════════════════════════╗
  // ║  ⭐⭐⭐ NEW: LOAD COMPANY PROFILE FROM MYACCOUNT ⭐⭐⭐       ║
  // ╚═══════════════════════════════════════════════════════════════════╝
  
  useEffect(() => {
    const loadCompanyProfile = async () => {
      if (!companyId) {
        console.log('❌ No company ID provided for profile loading');
        setProfileLoading(false);
        return;
      }

      try {
        setProfileLoading(true);
        console.log('🔍 Loading company profile for customer form...', companyId);
        
        // Query userProfiles collection for any profile belonging to this company
        const userProfilesRef = collection(db, 'userProfiles');
        const profileQuery = query(userProfilesRef, where('companyId', '==', companyId));
        const profileSnapshot = await getDocs(profileQuery);
        
        if (!profileSnapshot.empty) {
          // Get the first profile for this company (any admin/user profile will have the company branding)
          const firstProfile = profileSnapshot.docs[0].data();
          console.log('✅ Loaded company profile from MyAccount:', firstProfile);
          
          setProfileData(firstProfile);
          
          // Update company branding with profile data
          setCompanyBranding({
            logoUrl: firstProfile.profilePicture || null,
            displayName: firstProfile.rentalName || companyName || 'Motorcycle Rentals',
            address: firstProfile.rentalAddress || '',
            phone: firstProfile.phoneNumbers?.[0] || '',
            email: firstProfile.emails?.[0] || '',
            website: firstProfile.websites?.[0] || '',
            businessRegNumber: firstProfile.businessRegistrationNumber || ''
          });
          
          console.log('✅ Company branding loaded from MyAccount profile');
        } else {
          console.log('⚠️ No user profile found for company, using defaults');
          setProfileData(null);
          setCompanyBranding({
            logoUrl: null,
            displayName: companyName || 'Motorcycle Rentals',
            address: '',
            phone: '',
            email: '',
            website: '',
            businessRegNumber: ''
          });
        }
      } catch (error) {
        console.error('❌ Error loading company profile:', error);
        setProfileData(null);
        setCompanyBranding({
          logoUrl: null,
          displayName: companyName || 'Motorcycle Rentals',
          address: '',
          phone: '',
          email: '',
          website: '',
          businessRegNumber: ''
        });
      } finally {
        setProfileLoading(false);
      }
    };

    loadCompanyProfile();
  }, [companyId, companyName]);

  // Get unique bike models with availability info
  const availableBikeModels = useMemo(() => {
    const modelMap = {};
    
    availableBikes.forEach(bike => {
      const isAvailable = bikeAvailabilityMap[bike.regNumber] !== false;
      
      if (!modelMap[bike.model]) {
        modelMap[bike.model] = {
          model: bike.model,
          dailyRate: bike.dailyRate || 25,
          totalCount: 0,
          availableCount: 0
        };
      }
      
      modelMap[bike.model].totalCount++;
      if (isAvailable) {
        modelMap[bike.model].availableCount++;
      }
    });

    return Object.values(modelMap)
      .filter(m => m.availableCount > 0)
      .sort((a, b) => a.model.localeCompare(b.model));
  }, [availableBikes, bikeAvailabilityMap]);

  // Get total and available bike counts
  const bikeStats = useMemo(() => {
    const total = availableBikes.length;
    const available = availableBikes.filter(bike => 
      bikeAvailabilityMap[bike.regNumber] !== false
    ).length;
    const unavailable = total - available;
    
    return { total, available, unavailable };
  }, [availableBikes, bikeAvailabilityMap]);

  // Get enabled addons
  const getEnabledAddons = () => {
    return (reservationSettings.rentalAddons || []).filter(addon => addon && addon.enabled === true);
  };

  // Get enabled documents
  const getEnabledDocuments = () => {
    return (reservationSettings.documentRequirements || []).filter(doc => doc && doc.enabled === true);
  };

  // Get required documents
  const getRequiredDocuments = () => {
    const documents = getEnabledDocuments();
    return documents.filter(doc => doc.required === true);
  };

  // Calculate addon costs
  const calculateAddonCosts = (addons, days) => {
    if (!addons || addons.length === 0 || !days) return 0;
    
    return addons.reduce((total, addonId) => {
      const addon = getEnabledAddons().find(a => a.id === addonId);
      if (!addon) return total;
      
      const price = parseFloat(addon.price) || 0;
      if (addon.pricingMethod === 'perDay') {
        return total + (price * days);
      } else {
        return total + price;
      }
    }, 0);
  };

  // Handle addon selection
  const handleAddonChange = (addonId, checked) => {
    setSelectedAddons(prev => {
      if (checked) {
        return [...prev, addonId];
      } else {
        return prev.filter(id => id !== addonId);
      }
    });
  };

  // Handle document upload/selection
  const handleDocumentChange = (documentId, file) => {
    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: file
    }));
    
    if (file) {
      setDocumentValidation(prev => ({
        ...prev,
        [documentId]: { isValid: true, message: '' }
      }));
    }
  };

  // Validate document requirements
  const validateDocuments = () => {
    const requiredDocs = getRequiredDocuments();
    const errors = {};
    let hasErrors = false;

    requiredDocs.forEach(doc => {
      if (!documentUploads[doc.id]) {
        errors[doc.id] = {
          isValid: false,
          message: `${doc.name} is required`
        };
        hasErrors = true;
      } else {
        errors[doc.id] = {
          isValid: true,
          message: ''
        };
      }
    });

    setDocumentValidation(errors);
    return !hasErrors;
  };

  // Upload document to Firebase Storage
  const uploadDocumentToStorage = async (file, documentId, documentName, reservationNum) => {
    try {
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const sanitizedDocName = documentName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${sanitizedDocName}_${timestamp}.${fileExtension}`;
      
      const storageRef = ref(
        storage, 
        `companies/${companyId}/reservations/${reservationNum}/documents/${fileName}`
      );
      
      console.log(`  ⬆️  Uploading ${fileName}...`);
      
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: { status: 'uploading', progress: 0 }
      }));
      
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          documentId: documentId.toString(),
          documentName: documentName,
          originalFileName: file.name,
          reservationNumber: reservationNum,
          uploadedAt: new Date().toISOString()
        }
      });
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: { status: 'completed', progress: 100, url: downloadURL }
      }));
      
      console.log(`  ✅ ${fileName} uploaded successfully`);
      
      return {
        downloadURL,
        storagePath: snapshot.ref.fullPath,
        fileName: fileName,
        originalFileName: file.name
      };
    } catch (error) {
      console.error(`  ❌ Error uploading document:`, error);
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: { status: 'error', progress: 0, error: error.message }
      }));
      throw error;
    }
  };

  // Load company settings and bikes
  useEffect(() => {
    const loadCompanyData = async () => {
      if (!companyId) {
        setError('Company not specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        if (settingsDocRef) {
          const settingsSnap = await getDoc(settingsDocRef);
          if (settingsSnap.exists()) {
            const settings = settingsSnap.data();
            
            if (!settings.depositOptions && settings.defaultDepositAmount) {
              settings.depositOptions = [{
                id: 1,
                amount: settings.defaultDepositAmount.replace(/[^0-9.]/g, ''),
                currency: 'USD',
                symbol: '$',
                label: settings.defaultDepositAmount,
                isDefault: true
              }];
            }

            if (!Array.isArray(settings.rentalAddons)) {
              settings.rentalAddons = [];
            }

            if (!Array.isArray(settings.documentRequirements)) {
              settings.documentRequirements = [];
            }

            // Ensure branding settings exist with all fields
            if (!settings.branding) {
              settings.branding = {
                showLogo: true,
                showCompanyName: true,
                logoSize: 'medium',
                logoPosition: 'center',
                nameSize: 'medium',
                namePosition: 'center',
                nameColor: 'from-orange-400 via-red-500 to-purple-600',
                showBusinessRegNumber: true,
                businessRegNumberColor: 'text-gray-400',
                showAddress: true,
                addressColor: 'text-gray-400'
              };
            } else {
              // Ensure color properties exist on existing branding settings
              if (!settings.branding.nameColor) {
                settings.branding.nameColor = 'from-orange-400 via-red-500 to-purple-600';
              }
              if (!settings.branding.businessRegNumberColor) {
                settings.branding.businessRegNumberColor = 'text-gray-400';
              }
              if (!settings.branding.addressColor) {
                settings.branding.addressColor = 'text-gray-400';
              }
            }
            
            setReservationSettings(prev => ({ ...prev, ...settings }));
          }
        }

        const bikesQuery = query(managedBikesCollection, where('companyId', '==', companyId));
        const bikesSnapshot = await getDocs(bikesQuery);
        const bikes = bikesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAvailableBikes(bikes);

        setFormData(prev => ({
          ...prev,
          pickupLocation: reservationSettings.defaultPickupLocation || 'SLBR',
          dropoffLocation: reservationSettings.defaultDropoffLocation || 'SLBR',
          depositAmount: reservationSettings.depositOptions?.[0]?.label || '$100'
        }));

      } catch (error) {
        console.error('Error loading company data:', error);
        setError('Failed to load booking information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCompanyData();
  }, [companyId, managedBikesCollection, settingsDocRef]);

  // Update addon costs when selection or calculation details change
  useEffect(() => {
    const costs = calculateAddonCosts(selectedAddons, calculationDetails.days);
    setAddonCosts(costs);
  }, [selectedAddons, calculationDetails.days, reservationSettings.rentalAddons]);

  // Calculate rental amount based on bike MODEL
  const calculateRentalAmount = (bikeModel, pickupDateTime, dropoffDateTime) => {
    if (!bikeModel || !pickupDateTime || !dropoffDateTime) {
      setCalculatedAmount(0);
      setCalculationDetails({});
      return 0;
    }

    const modelBike = availableBikes.find(bike => 
      bike.model === bikeModel && bikeAvailabilityMap[bike.regNumber] !== false
    );

    if (!modelBike) {
      setCalculatedAmount(0);
      setCalculationDetails({});
      return 0;
    }

    const startDate = new Date(pickupDateTime);
    const endDate = new Date(dropoffDateTime);
    
    let days;
    if (reservationSettings.rentalCalculationMethod === 'inclusive') {
      const startCalendarDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endCalendarDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      const timeDiff = endCalendarDate.getTime() - startCalendarDate.getTime();
      days = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
    } else {
      const timeDiff = endDate.getTime() - startDate.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      days = hoursDiff <= 24 ? 1 : Math.ceil(hoursDiff / 24);
    }

    if (days < reservationSettings.minimumRentalDays) {
      days = reservationSettings.minimumRentalDays;
    }

    const dailyRate = modelBike.dailyRate || 25;
    const totalAmount = days * dailyRate;

    setCalculatedAmount(totalAmount);
    setCalculationDetails({
      days,
      dailyRate,
      bikeModel: bikeModel,
      bikeReg: 'To be assigned'
    });

    return totalAmount;
  };

  // Check availability for all bikes
  const checkAllBikesAvailability = async (pickupDateTime, dropoffDateTime) => {
    if (!pickupDateTime || !dropoffDateTime || !reservationsCollection || availableBikes.length === 0) {
      setBikeAvailabilityMap({});
      setModelAvailabilityMap({});
      return;
    }

    try {
      setCheckingAvailability(true);

      const newStart = new Date(pickupDateTime);
      const newEnd = new Date(dropoffDateTime);

      const reservationsQuery = query(
        reservationsCollection,
        where('availabilityStatus', 'in', ['booked', 'pending'])
      );
      const reservationsSnapshot = await getDocs(reservationsQuery);

      const unavailableBikes = new Set();
      const unavailableModels = new Set();
      
      reservationsSnapshot.forEach((doc) => {
        const reservation = doc.data();
        
        if (reservation.availabilityStatus === 'cancel') return;

        const reservationStart = new Date(reservation.pickupDateTime);
        const reservationEnd = new Date(reservation.dropoffDateTime);

        if (reservationStart <= newEnd && reservationEnd >= newStart) {
          if (reservation.motorbikeNumber) {
            unavailableBikes.add(reservation.motorbikeNumber);
          }
          if (reservation.bikeModel && !reservation.motorbikeNumber) {
            unavailableModels.add(reservation.bikeModel);
          }
        }
      });

      const availabilityMap = {};
      const modelCountMap = {};
      
      availableBikes.forEach(bike => {
        const isAvailable = !unavailableBikes.has(bike.regNumber);
        availabilityMap[bike.regNumber] = isAvailable;
        
        if (!modelCountMap[bike.model]) {
          modelCountMap[bike.model] = { total: 0, available: 0 };
        }
        modelCountMap[bike.model].total++;
        if (isAvailable) {
          modelCountMap[bike.model].available++;
        }
      });

      setBikeAvailabilityMap(availabilityMap);
      setModelAvailabilityMap(modelCountMap);

    } catch (error) {
      console.error('Error checking bike availability:', error);
      const availabilityMap = {};
      availableBikes.forEach(bike => {
        availabilityMap[bike.regNumber] = true;
      });
      setBikeAvailabilityMap(availabilityMap);
      setModelAvailabilityMap({});
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'pickupDateTime' || name === 'dropoffDateTime') {
      const updatedData = { ...formData, [name]: value };
      
      if (updatedData.pickupDateTime && updatedData.dropoffDateTime) {
        checkAllBikesAvailability(updatedData.pickupDateTime, updatedData.dropoffDateTime);
        
        if (selectedBikeModel) {
          calculateRentalAmount(
            selectedBikeModel,
            updatedData.pickupDateTime,
            updatedData.dropoffDateTime
          );
        }
      }
    }
  };

  // Handle bike model selection
  const handleBikeModelChange = (e) => {
    const model = e.target.value;
    setSelectedBikeModel(model);
    setFormData(prev => ({ ...prev, bikeModel: model }));
    
    if (model && formData.pickupDateTime && formData.dropoffDateTime) {
      calculateRentalAmount(model, formData.pickupDateTime, formData.dropoffDateTime);
    } else {
      setCalculatedAmount(0);
      setCalculationDetails({});
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = [];

    if (!formData.customerName.trim()) errors.push('Name is required');
    if (reservationSettings.requireCustomerEmail && !formData.email.trim()) errors.push('Email is required');
    if (reservationSettings.requireLicenseNumber && !formData.licenseNumber.trim()) errors.push('License number is required');
    if (!formData.pickupDateTime) errors.push('Pickup date and time is required');
    if (!formData.dropoffDateTime) errors.push('Return date and time is required');
    if (!formData.bikeModel) errors.push('Please select a bike model');

    if (!reservationSettings.allowSameDayRental) {
      const pickupDate = new Date(formData.pickupDateTime).toDateString();
      const dropoffDate = new Date(formData.dropoffDateTime).toDateString();
      if (pickupDate === dropoffDate) {
        errors.push('Same-day rentals are not allowed');
      }
    }

    const requiredDocs = getRequiredDocuments();
    const missingDocs = requiredDocs.filter(doc => !documentUploads[doc.id]);
    
    if (missingDocs.length > 0) {
      errors.push(`Please upload required documents: ${missingDocs.map(doc => doc.name).join(', ')}`);
    }

    return errors;
  };

  // Submit reservation
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    if (!validateDocuments()) {
      setError('Please upload all required documents before submitting your booking.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const reservationNum = `BK${Date.now().toString().slice(-6)}`;

      console.log('📝 Starting MODEL-BASED reservation submission...');
      console.log('🏍️  Booked Model:', selectedBikeModel);

      // Upload documents
      const documentData = {};
      const enabledDocs = getEnabledDocuments();
      
      if (enabledDocs.length > 0 && Object.keys(documentUploads).length > 0) {
        console.log('⬆️  Uploading documents...');
        
        for (const doc of enabledDocs) {
          if (documentUploads[doc.id]) {
            const file = documentUploads[doc.id];
            
            try {
              const uploadResult = await uploadDocumentToStorage(
                file, 
                doc.id, 
                doc.name, 
                reservationNum
              );
              
              documentData[doc.id] = {
                documentId: doc.id,
                documentName: doc.name,
                category: doc.category,
                required: doc.required,
                fileName: uploadResult.fileName,
                originalFileName: uploadResult.originalFileName,
                fileSize: file.size,
                fileType: file.type,
                downloadURL: uploadResult.downloadURL,
                storagePath: uploadResult.storagePath,
                uploadedAt: new Date().toISOString(),
                status: 'uploaded'
              };
              
            } catch (uploadError) {
              console.error(`  ❌ Error uploading ${doc.name}:`, uploadError);
              throw new Error(`Failed to upload ${doc.name}. Please try again.`);
            }
          }
        }
      }

      // Prepare add-ons data
      const selectedAddonData = selectedAddons.map(addonId => {
        const addon = getEnabledAddons().find(a => a.id === addonId);
        if (!addon) return null;
        const cost = addon.pricingMethod === 'perDay' 
          ? (parseFloat(addon.price) * calculationDetails.days)
          : parseFloat(addon.price);
        return {
          id: addon.id,
          name: addon.name,
          category: addon.category,
          price: addon.price,
          pricingMethod: addon.pricingMethod,
          calculatedCost: cost.toFixed(2)
        };
      }).filter(Boolean);

      // Create reservation data
      const reservationData = {
        customerName: formData.customerName,
        email: formData.email,
        whatsappNumber: formData.whatsappNumber,
        localNumber: formData.localNumber,
        licenseNumber: formData.licenseNumber,
        pickupDateTime: formData.pickupDateTime,
        dropoffDateTime: formData.dropoffDateTime,
        pickupLocation: formData.pickupLocation,
        dropoffLocation: formData.dropoffLocation,
        depositAmount: formData.depositAmount,
        note: formData.note,
        bikeModel: selectedBikeModel,
        motorbikeNumber: '',
        bikeAssignmentStatus: 'pending',
        bikeAssignedDate: null,
        assignedBy: null,
        availabilityStatus: 'pending',
        rentalAmount: calculatedAmount.toString(),
        subtotalAmount: calculatedAmount.toString(),
        totalAmount: (calculatedAmount + addonCosts).toString(),
        selectedAddons: selectedAddonData,
        addonCosts: addonCosts.toString(),
        documentRequirements: enabledDocs,
        uploadedDocuments: documentData,
        documentValidationStatus: Object.keys(documentData).length >= getRequiredDocuments().length ? 'complete' : 'pending',
        documentsUploadedCount: Object.keys(documentData).length,
        documentsRequiredCount: getRequiredDocuments().length,
        documentsStoredInFirebase: Object.keys(documentData).length > 0,
        rentalPaymentStatus: 'not paid',
        drivingLicenseStatus: 'not applied',
        drivingLicensePaymentStatus: 'not paid',
        reservationNumber: reservationNum,
        customerBooking: true,
        companyId: companyId,
        companyName: companyName,
        calculationDetails: calculationDetails,
        appliedSettings: {
          calculationMethod: reservationSettings.rentalCalculationMethod,
          minimumDays: reservationSettings.minimumRentalDays,
          depositOptions: reservationSettings.depositOptions,
          enabledAddons: getEnabledAddons(),
          enabledDocuments: getEnabledDocuments()
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(reservationsCollection, reservationData);
      
      setReservationNumber(reservationNum);
      setSubmitted(true);
      setStep(4);

    } catch (error) {
      console.error('❌ Error submitting reservation:', error);
      setError(error.message || 'Failed to submit reservation. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadProgress({});
    }
  };

  // Navigation between steps
  const nextStep = () => {
    if (step === 1) {
      if (!formData.customerName || !formData.email) {
        setError('Please fill in your contact information');
        return;
      }
    } else if (step === 2) {
      if (!formData.pickupDateTime || !formData.dropoffDateTime || !formData.bikeModel) {
        setError('Please complete your booking details');
        return;
      }
      if (getRequiredDocuments().length > 0 && !validateDocuments()) {
        setError('Please upload all required documents before proceeding');
        return;
      }
    }
    
    setError(null);
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
    setError(null);
  };

  // Helper function to check if color is a gradient
  const isGradient = (colorValue) => {
    return colorValue && (colorValue.includes('from-') || colorValue.startsWith('linear-gradient'));
  };

  // Helper function to check if color is custom (hex, rgb, or linear-gradient)
  const isCustomColor = (colorValue) => {
    return colorValue && (colorValue.startsWith('#') || colorValue.startsWith('rgb') || colorValue.startsWith('linear-gradient'));
  };

  // Get text style for custom or Tailwind colors
  const getTextStyle = (colorValue, isLargeText = false) => {
    if (!colorValue) return {};
    
    if (isGradient(colorValue)) {
      if (isCustomColor(colorValue)) {
        // Custom gradient (linear-gradient string)
        return {
          background: colorValue,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        };
      } else {
        // Tailwind gradient - handled by className
        return {};
      }
    } else if (isCustomColor(colorValue)) {
      // Custom solid color (hex or rgb)
      return { color: colorValue };
    }
    
    // Tailwind class - handled by className
    return {};
  };

  // Get className for Tailwind colors
  const getTextClassName = (colorValue, baseClasses = '') => {
    if (!colorValue || isCustomColor(colorValue)) {
      // Custom colors are handled by inline styles
      return baseClasses;
    }
    
    if (isGradient(colorValue)) {
      // Tailwind gradient
      return `${baseClasses} text-transparent bg-clip-text bg-gradient-to-r ${colorValue}`;
    }
    
    // Tailwind solid color class
    return `${baseClasses} ${colorValue}`;
  };

  // LOADING STATE
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading booking form...</p>
          {profileLoading && (
            <p className="text-gray-400 text-sm mt-2">Loading company branding...</p>
          )}
        </div>
      </div>
    );
  }

  // SUCCESS/SUBMITTED STATE
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          {/* ⭐⭐⭐ Company Branding in Success Screen from MyAccount ⭐⭐⭐ */}
          {(reservationSettings.branding?.showLogo || 
            reservationSettings.branding?.showCompanyName || 
            reservationSettings.branding?.showBusinessRegNumber || 
            reservationSettings.branding?.showAddress) && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              {reservationSettings.branding?.showLogo && companyBranding.logoUrl && (
                <div className="flex justify-center mb-3">
                  <img
                    src={companyBranding.logoUrl}
                    alt={`${companyBranding.displayName} Logo`}
                    className="h-20 object-contain rounded-lg"
                    onError={(e) => {
                      console.error('Failed to load company logo in success screen');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {reservationSettings.branding?.showCompanyName && (
                <h2 
                  className={getTextClassName(
                    reservationSettings.branding?.nameColor || 'from-orange-400 via-red-500 to-purple-600',
                    'text-xl font-bold'
                  )}
                  style={getTextStyle(reservationSettings.branding?.nameColor || 'from-orange-400 via-red-500 to-purple-600', true)}
                >
                  {companyBranding.displayName}
                </h2>
              )}
              {/* Business Registration Number - Independent Display */}
              {reservationSettings.branding?.showBusinessRegNumber === true && companyBranding.businessRegNumber && (
                <p 
                  className={getTextClassName(
                    reservationSettings.branding?.businessRegNumberColor || 'text-gray-500',
                    'text-xs mt-1 flex items-center gap-1 justify-center'
                  )}
                  style={getTextStyle(reservationSettings.branding?.businessRegNumberColor || 'text-gray-500')}
                >
                  <Building size={10} />
                  Reg: {companyBranding.businessRegNumber}
                </p>
              )}
              {/* Company Address - Independent Display */}
              {reservationSettings.branding?.showAddress === true && companyBranding.address && (
                <p 
                  className={getTextClassName(
                    reservationSettings.branding?.addressColor || 'text-gray-500',
                    'text-xs mt-1 text-center'
                  )}
                  style={getTextStyle(reservationSettings.branding?.addressColor || 'text-gray-500')}
                >
                  {companyBranding.address}
                </p>
              )}
            </div>
          )}
          
          <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Booking Submitted!</h2>
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">Your reservation number:</p>
            <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">{reservationNumber}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800 font-medium mb-1">
              Bike Model Reserved: {selectedBikeModel}
            </p>
            <p className="text-xs text-blue-600">
              We'll assign your specific bike registration number before pickup
            </p>
          </div>
          <p className="text-gray-600 mb-6">
            Thank you for your booking! We'll review your reservation and contact you within 24 hours to confirm your booking and assign your specific bike.
          </p>
          <div className="space-y-2 text-sm text-gray-500">
            <p>• Check your email for confirmation details</p>
            <p>• Save your reservation number for reference</p>
            <p>• We'll call you to confirm pickup details</p>
            <p>• Specific bike will be assigned before your pickup date</p>
            {selectedAddons.length > 0 && (
              <p>• Add-ons will be prepared for your pickup</p>
            )}
            {Object.keys(documentUploads).length > 0 && (
              <p>• Documents have been securely uploaded</p>
            )}
          </div>
          {/* ⭐ Contact info from MyAccount */}
          {(companyBranding.phone || companyBranding.email) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Contact us:</p>
              {companyBranding.phone && (
                <p className="text-sm text-gray-700 flex items-center justify-center gap-1">
                  <Phone size={14} />
                  {companyBranding.phone}
                </p>
              )}
              {companyBranding.email && (
                <p className="text-sm text-gray-700 flex items-center justify-center gap-1 mt-1">
                  <Mail size={14} />
                  {companyBranding.email}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // MAIN FORM RENDER - START
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900">
      {/* ╔═══════════════════════════════════════════════════════════════════╗ */}
      {/* ║  ⭐⭐⭐ HEADER WITH MYACCOUNT COMPANY BRANDING ⭐⭐⭐         ║ */}
      {/* ╚═══════════════════════════════════════════════════════════════════╝ */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Company Branding from MyAccount */}
          {(reservationSettings.branding?.showLogo || 
            reservationSettings.branding?.showCompanyName || 
            reservationSettings.branding?.showBusinessRegNumber || 
            reservationSettings.branding?.showAddress) && (
            <div className="mb-6 pb-6 border-b border-gray-700">
              {/* Logo from MyAccount Profile Picture */}
              {reservationSettings.branding?.showLogo && companyBranding.logoUrl && (
                <div className={`flex mb-4 ${
                  reservationSettings.branding?.logoPosition === 'left' ? 'justify-start' :
                  reservationSettings.branding?.logoPosition === 'right' ? 'justify-end' :
                  'justify-center'
                }`}>
                  <img
                    src={companyBranding.logoUrl}
                    alt={`${companyBranding.displayName} Logo`}
                    className={`${
                      reservationSettings.branding?.logoSize === 'small' ? 'h-12' :
                      reservationSettings.branding?.logoSize === 'large' ? 'h-24' :
                      'h-16'
                    } object-contain transition-all duration-300 rounded-lg`}
                    onError={(e) => {
                      console.error('Failed to load company logo');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              {/* Company Name from MyAccount Rental Name */}
              {reservationSettings.branding?.showCompanyName && (
                <div className={`${
                  reservationSettings.branding?.namePosition === 'left' ? 'text-left' :
                  reservationSettings.branding?.namePosition === 'right' ? 'text-right' :
                  'text-center'
                }`}>
                  <h1 
                    className={getTextClassName(
                      reservationSettings.branding?.nameColor || 'from-orange-400 via-red-500 to-purple-600',
                      `font-bold ${
                        reservationSettings.branding?.nameSize === 'small' ? 'text-xl' :
                        reservationSettings.branding?.nameSize === 'large' ? 'text-4xl' :
                        'text-2xl'
                      }`
                    )}
                    style={getTextStyle(reservationSettings.branding?.nameColor || 'from-orange-400 via-red-500 to-purple-600', true)}
                  >
                    {companyBranding.displayName}
                  </h1>
                </div>
              )}
              
              {/* Business Registration Number - Independent Display */}
              {reservationSettings.branding?.showBusinessRegNumber === true && companyBranding.businessRegNumber && (
                <p 
                  className={getTextClassName(
                    reservationSettings.branding?.businessRegNumberColor || 'text-gray-400',
                    'text-xs mt-1 flex items-center gap-1 justify-center'
                  )}
                  style={getTextStyle(reservationSettings.branding?.businessRegNumberColor || 'text-gray-400')}
                >
                  <Building size={10} />
                  Reg: {companyBranding.businessRegNumber}
                </p>
              )}
              
              {/* Company Address - Independent Display */}
              {reservationSettings.branding?.showAddress === true && companyBranding.address && (
                <p 
                  className={getTextClassName(
                    reservationSettings.branding?.addressColor || 'text-gray-400',
                    'text-xs mt-1 text-center'
                  )}
                  style={getTextStyle(reservationSettings.branding?.addressColor || 'text-gray-400')}
                >
                  {companyBranding.address}
                </p>
              )}
            </div>
          )}
          
          {/* Existing header content */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600 flex items-center">
                <Bike className="w-7 h-7 text-orange-500 mr-2" />
                Book Your Ride
              </h2>
              {!reservationSettings.branding?.showCompanyName && (
                <p className="text-gray-400 mt-1">{companyBranding.displayName}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {[1, 2, 3].map((stepNum) => (
                <div
                  key={stepNum}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= stepNum
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                      : 'bg-gray-700 text-gray-400 border border-gray-600'
                  }`}
                >
                  {stepNum}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-900/30 border border-red-700 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Step 1: Contact Information */}
          {step === 1 && (
            <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2 flex items-center">
                  <User className="w-6 h-6 text-orange-500 mr-2" />
                  Contact Information
                </h2>
                <p className="text-gray-400">Let us know how to reach you</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address {reservationSettings.requireCustomerEmail && <span className="text-red-400">*</span>}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="your@email.com"
                      required={reservationSettings.requireCustomerEmail}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">WhatsApp Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="whatsappNumber"
                      value={formData.whatsappNumber}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    International Driving License Number {reservationSettings.requireLicenseNumber && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter license number"
                    required={reservationSettings.requireLicenseNumber}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button
                  type="button"
                  onClick={nextStep}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors flex items-center shadow-lg"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Booking Details */}
          {step === 2 && (
            <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2 flex items-center">
                  <Calendar className="w-6 h-6 text-orange-500 mr-2" />
                  Booking Details
                </h2>
                <p className="text-gray-400">Choose your bike model and rental period</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Pickup Date & Time <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="datetime-local"
                      name="pickupDateTime"
                      value={formData.pickupDateTime}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Return Date & Time <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="datetime-local"
                      name="dropoffDateTime"
                      value={formData.dropoffDateTime}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* MODEL-BASED Bike Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Choose Your Bike Model <span className="text-red-400">*</span>
                  {checkingAvailability && (
                    <span className="ml-2 text-sm text-orange-400">(Checking availability...)</span>
                  )}
                </label>

                {/* Prompt to select dates first */}
                {(!formData.pickupDateTime || !formData.dropoffDateTime) && (
                  <div className="mb-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-yellow-300">
                        Please select pickup and return dates above to see available bike models.
                      </div>
                    </div>
                  </div>
                )}

                {/* Select Bike Model - SINGLE DROPDOWN */}
                <div className="relative">
                  <Bike className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedBikeModel}
                    onChange={handleBikeModelChange}
                    disabled={!formData.pickupDateTime || !formData.dropoffDateTime}
                    className="w-full pl-10 pr-10 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">-- Select Bike Model --</option>
                    {availableBikeModels.length === 0 && formData.pickupDateTime && formData.dropoffDateTime ? (
                      <option value="" disabled>No bikes available for selected dates</option>
                    ) : (
                      availableBikeModels.map((modelInfo) => (
                        <option key={modelInfo.model} value={modelInfo.model}>
                          {modelInfo.model} - ${modelInfo.dailyRate}/day ({modelInfo.availableCount} available)
                        </option>
                      ))
                    )}
                  </select>
                  <ChevronDown className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>

                {/* Selected Model Info Display */}
                {selectedBikeModel && calculationDetails.bikeModel && (
                  <div className="mt-4 bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Bike className="w-6 h-6 text-orange-500 mr-3" />
                        <div>
                          <div className="font-medium text-white">{calculationDetails.bikeModel}</div>
                          <div className="text-sm text-gray-400">
                            Specific bike will be assigned before pickup
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-400">Daily Rate</div>
                        <div className="text-lg font-bold text-orange-400">${calculationDetails.dailyRate}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Security Deposit Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Security Deposit <span className="text-red-400">*</span>
                </label>
                {reservationSettings.depositOptions?.length > 1 ? (
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <select
                      name="depositAmount"
                      value={formData.depositAmount}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-10 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none cursor-pointer"
                    >
                      {reservationSettings.depositOptions.map(deposit => (
                        <option key={deposit.id} value={deposit.label}>
                          {deposit.label} {deposit.isDefault ? '(Recommended)' : ''} - {deposit.currency}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                ) : (
                  <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-3 flex items-center">
                    <DollarSign className="w-5 h-5 text-gray-400 mr-2" />
                    <div>
                      <span className="font-medium text-white">{formData.depositAmount}</span>
                      <span className="text-sm text-gray-400 ml-2">(Refundable security deposit)</span>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  The security deposit will be refunded after the bike is returned in good condition
                </p>
              </div>

              {/* Add-ons Selection */}
              {getEnabledAddons().length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Optional Add-ons
                    <div className="flex items-center text-xs text-gray-400 mt-1">
                      <Shield size={12} className="mr-1" />
                      <span>{getEnabledAddons().length} clothing & equipment options available</span>
                    </div>
                  </label>
                  
                  <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getEnabledAddons().map((addon) => (
                        <label
                          key={addon.id}
                          className={`relative cursor-pointer rounded-lg border p-3 hover:border-orange-400 transition-colors ${
                            selectedAddons.includes(addon.id)
                              ? 'border-orange-500 bg-orange-900/30'
                              : 'border-gray-600 bg-gray-700/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAddons.includes(addon.id)}
                            onChange={(e) => handleAddonChange(addon.id, e.target.checked)}
                            className="sr-only"
                          />
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <Shield className="w-4 h-4 text-orange-500 mr-2" />
                              <div>
                                <div className="font-medium text-white text-sm">{addon.name}</div>
                                <div className="text-xs text-gray-400">{addon.category}</div>
                                <div className="text-sm font-medium text-orange-400">
                                  ${addon.price} {addon.pricingMethod === 'perDay' ? '/day' : 'flat'}
                                </div>
                              </div>
                            </div>
                            {selectedAddons.includes(addon.id) && (
                              <CheckCircle className="w-4 h-4 text-orange-500" />
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    
                    {selectedAddons.length > 0 && (
                      <div className="mt-3 bg-gray-800/50 rounded p-3 border border-gray-600">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-white">Selected Add-ons Cost:</span>
                          <span className="text-orange-400 font-bold">${addonCosts.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {selectedAddons.map(addonId => {
                            const addon = getEnabledAddons().find(a => a.id === addonId);
                            if (!addon) return null;
                            const cost = addon.pricingMethod === 'perDay' 
                              ? (parseFloat(addon.price) * calculationDetails.days)
                              : parseFloat(addon.price);
                            return `${addon.name}: $${cost.toFixed(2)}`;
                          }).filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Document Requirements */}
              {getEnabledDocuments().length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                      <Camera size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Document Upload</h3>
                      <p className="text-sm text-gray-400">
                        {getRequiredDocuments().length} required • {getEnabledDocuments().length - getRequiredDocuments().length} optional
                      </p>
                    </div>
                  </div>
                  
                  {/* Instructions Card */}
                  <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 border border-blue-500/30 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-blue-300 font-medium text-sm mb-2">Upload Guidelines</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs text-blue-400">
                          <span>• Clear photos/scans</span>
                          <span>• JPG, PNG, PDF formats</span>
                          <span>• Max 5MB per file</span>
                          <span>• All info visible</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Upload Progress Indicator during submission */}
                  {submitting && Object.keys(uploadProgress).length > 0 && (
                    <div className="mb-4 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Loader className="w-4 h-4 animate-spin text-blue-400" />
                        <span className="text-sm font-medium text-blue-300">Uploading documents...</span>
                      </div>
                      <div className="space-y-2">
                        {Object.entries(uploadProgress).map(([docId, progress]) => {
                          const doc = getEnabledDocuments().find(d => d.id.toString() === docId);
                          if (!doc) return null;
                          return (
                            <div key={docId} className="flex items-center justify-between text-xs">
                              <span className="text-gray-300">{doc.name}</span>
                              <span className={`
                                ${progress.status === 'uploading' ? 'text-blue-400' : ''}
                                ${progress.status === 'completed' ? 'text-green-400' : ''}
                                ${progress.status === 'error' ? 'text-red-400' : ''}
                              `}>
                                {progress.status === 'uploading' && '⏳ Uploading...'}
                                {progress.status === 'completed' && '✓ Uploaded'}
                                {progress.status === 'error' && '✗ Failed'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Document Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getEnabledDocuments().map((document) => {
                      const hasFile = documentUploads[document.id];
                      const validation = documentValidation[document.id];
                      const hasError = validation && !validation.isValid;
                      
                      return (
                        <div 
                          key={document.id} 
                          className={`relative rounded-xl border transition-all duration-200 hover:scale-[1.02] ${
                            hasError 
                              ? 'border-red-500/50 bg-red-900/10 shadow-red-500/10' 
                              : hasFile 
                                ? 'border-green-500/50 bg-green-900/10 shadow-green-500/10' 
                                : document.required
                                  ? 'border-orange-500/50 bg-orange-900/10 shadow-orange-500/10'
                                  : 'border-gray-600/50 bg-gray-700/20'
                          } shadow-lg`}
                        >
                          {/* Document Header */}
                          <div className="p-4 pb-3">
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                document.category === 'License' ? 'bg-blue-500/20 text-blue-400' :
                                document.category === 'Identity' ? 'bg-green-500/20 text-green-400' :
                                document.category === 'Travel' ? 'bg-purple-500/20 text-purple-400' :
                                document.category === 'Payment' ? 'bg-yellow-500/20 text-yellow-400' :
                                document.category === 'Insurance' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                <FileText size={16} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-white font-medium text-sm truncate">{document.name}</h4>
                                  {document.required && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-red-500 to-red-600 text-white">
                                      Required
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400">{document.category}</p>
                              </div>
                              
                              {hasFile && (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              )}
                              {hasError && (
                                <AlertCircle className="w-5 h-5 text-red-400" />
                              )}
                            </div>

                            {document.description && (
                              <p className="text-xs text-gray-500 mb-3">{document.description}</p>
                            )}

                            {/* Upload Zone */}
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleDocumentChange(document.id, e.target.files[0])}
                              className="hidden"
                              id={`doc-upload-${document.id}`}
                              disabled={submitting}
                            />
                            <label
                              htmlFor={`doc-upload-${document.id}`}
                              className={`flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all group ${
                                hasFile 
                                  ? 'border-green-500/50 bg-green-900/20 text-green-300' 
                                  : hasError
                                    ? 'border-red-500/50 bg-red-900/20 text-red-300 hover:border-red-400/70'
                                    : document.required
                                      ? 'border-orange-500/50 bg-orange-900/20 text-orange-300 hover:border-orange-400/70'
                                      : 'border-gray-500/50 bg-gray-800/20 text-gray-300 hover:border-blue-400/50 hover:bg-blue-900/10'
                              } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {hasFile ? (
                                <>
                                  <CheckCircle className="w-6 h-6 mb-2 text-green-400" />
                                  <div className="text-center">
                                    <p className="font-medium text-sm mb-1">{documentUploads[document.id].name}</p>
                                    <p className="text-xs opacity-75">
                                      {(documentUploads[document.id].size / 1024).toFixed(1)} KB • Ready to upload
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <Upload className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                                  <div className="text-center">
                                    <p className="font-medium text-sm mb-1">
                                      {document.required ? 'Upload Required' : 'Upload Optional'}
                                    </p>
                                    <p className="text-xs opacity-75">Click or drag file here</p>
                                  </div>
                                </>
                              )}
                            </label>

                            {/* File Actions */}
                            {hasFile && (
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-600/50">
                                <div className="flex items-center text-green-400 text-xs">
                                  <CheckCircle size={12} className="mr-1" />
                                  <span>Ready for upload</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDocumentChange(document.id, null)}
                                  className="text-red-400 hover:text-red-300 text-xs transition-colors"
                                  disabled={submitting}
                                >
                                  Remove
                                </button>
                              </div>
                            )}

                            {hasError && (
                              <div className="mt-3 bg-red-900/30 border border-red-700/50 rounded-lg p-2">
                                <p className="text-red-400 text-xs flex items-center">
                                  <AlertCircle size={12} className="mr-2" />
                                  {validation.message}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Progress Summary */}
                  <div className="mt-4 bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-xl p-4 border border-gray-600/50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-white font-medium text-sm">Upload Progress</h4>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-green-400">
                          {Object.keys(documentUploads).length}/{getEnabledDocuments().length} uploaded
                        </span>
                        {getRequiredDocuments().length > 0 && (
                          <span className={Object.keys(documentUploads).length >= getRequiredDocuments().length ? 'text-green-400' : 'text-red-400'}>
                            {Math.min(Object.keys(documentUploads).length, getRequiredDocuments().length)}/{getRequiredDocuments().length} required
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ${
                          Object.keys(documentUploads).length >= getRequiredDocuments().length 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-r from-orange-500 to-red-600'
                        }`}
                        style={{ 
                          width: `${(Object.keys(documentUploads).length / Math.max(getEnabledDocuments().length, 1)) * 100}%` 
                        }}
                      ></div>
                    </div>
                    
                    {getRequiredDocuments().length > 0 && Object.keys(documentUploads).length < getRequiredDocuments().length && (
                      <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle size={16} className="text-orange-400" />
                          <span className="text-orange-300">
                            Please upload all required documents to proceed with your booking
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Price Calculation */}
              {calculatedAmount > 0 && (
                <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500/50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-white flex items-center">
                      <Calculator className="w-5 h-5 mr-2 text-orange-400" />
                      Rental Summary
                    </h3>
                    <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                      ${calculatedAmount + addonCosts}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-300 mb-3">
                    <div>
                      <span className="font-medium">Duration:</span> {calculationDetails.days} {calculationDetails.days === 1 ? 'day' : 'days'}
                    </div>
                    <div>
                      <span className="font-medium">Daily Rate:</span> ${calculationDetails.dailyRate}
                    </div>
                  </div>
                  
                  {/* Cost Breakdown */}
                  <div className="bg-gray-800/30 rounded p-3">
                    <div className="text-xs text-gray-400 mb-2">Cost Breakdown:</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Bike Rental ({calculationDetails.bikeModel}):</span>
                        <span className="text-white">${calculatedAmount}</span>
                      </div>
                      {addonCosts > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-300">Add-ons:</span>
                          <span className="text-orange-400">${addonCosts.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-green-400 border-t border-gray-600 pt-1">
                        <span>Total:</span>
                        <span>${(calculatedAmount + addonCosts).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-700 text-gray-300 px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!formData.bikeModel}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Confirm */}
          {step === 3 && (
            <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2 flex items-center">
                  <CheckCircle className="w-6 h-6 text-orange-500 mr-2" />
                  Review & Confirm
                </h2>
                <p className="text-gray-400">Please review your booking details</p>
              </div>

              {/* Booking Summary */}
              <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-white mb-4">Booking Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Customer:</span>
                    <span className="ml-2 font-medium text-white">{formData.customerName}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Email:</span>
                    <span className="ml-2 font-medium text-white">{formData.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Bike Model:</span>
                    <span className="ml-2 font-medium text-white">{calculationDetails.bikeModel}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Specific Bike:</span>
                    <span className="ml-2 font-medium text-orange-400">To be assigned</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Duration:</span>
                    <span className="ml-2 font-medium text-white">{calculationDetails.days} {calculationDetails.days === 1 ? 'day' : 'days'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Pickup:</span>
                    <span className="ml-2 font-medium text-white">{new Date(formData.pickupDateTime).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Return:</span>
                    <span className="ml-2 font-medium text-white">{new Date(formData.dropoffDateTime).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Security Deposit:</span>
                    <span className="ml-2 font-medium text-white">{formData.depositAmount}</span>
                  </div>
                </div>
                
                {/* Show selected addons */}
                {selectedAddons.length > 0 && (
                  <div className="border-t border-gray-600 pt-4 mt-4">
                    <h4 className="text-white font-medium mb-3 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-orange-400" />
                      Selected Add-ons
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {selectedAddons.map(addonId => {
                        const addon = getEnabledAddons().find(a => a.id === addonId);
                        if (!addon) return null;
                        const cost = addon.pricingMethod === 'perDay' 
                          ? (parseFloat(addon.price) * calculationDetails.days)
                          : parseFloat(addon.price);
                        return (
                          <div key={addon.id} className="text-sm">
                            <span className="text-gray-400">{addon.name}:</span>
                            <span className="ml-2 font-medium text-orange-400">${cost.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="border-t border-gray-600 pt-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg font-medium text-white">Bike Rental:</span>
                    <span className="text-lg font-medium text-white">${calculatedAmount}</span>
                  </div>
                  {addonCosts > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg font-medium text-white">Add-ons:</span>
                      <span className="text-lg font-medium text-orange-400">${addonCosts.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t border-gray-600 pt-2">
                    <span className="text-xl font-bold text-white">Total Cost:</span>
                    <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">${(calculatedAmount + addonCosts).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Document Upload Summary */}
              {getEnabledDocuments().length > 0 && (
                <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-6 mb-6">
                  <h3 className="font-medium text-white mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-orange-400" />
                    Uploaded Documents
                  </h3>
                  
                  {Object.keys(documentUploads).length > 0 ? (
                    <div className="space-y-3">
                      {getEnabledDocuments().map(doc => {
                        const file = documentUploads[doc.id];
                        if (!file) return null;
                        
                        return (
                          <div key={doc.id} className="flex items-center justify-between bg-gray-800/50 rounded p-3">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded ${
                                doc.category === 'License' ? 'bg-blue-500/20' :
                                doc.category === 'Identity' ? 'bg-green-500/20' :
                                doc.category === 'Travel' ? 'bg-purple-500/20' :
                                doc.category === 'Payment' ? 'bg-yellow-500/20' :
                                doc.category === 'Insurance' ? 'bg-red-500/20' :
                                'bg-gray-500/20'
                              }`}>
                                <FileText className={`w-4 h-4 ${
                                  doc.category === 'License' ? 'text-blue-400' :
                                  doc.category === 'Identity' ? 'text-green-400' :
                                  doc.category === 'Travel' ? 'text-purple-400' :
                                  doc.category === 'Payment' ? 'text-yellow-400' :
                                  doc.category === 'Insurance' ? 'text-red-400' :
                                  'text-gray-400'
                                }`} />
                              </div>
                              <div>
                                <div className="font-medium text-white">{doc.name}</div>
                                <div className="text-sm text-gray-400">{file.name} • {(file.size / 1024).toFixed(1)} KB</div>
                              </div>
                            </div>
                            <div className="flex items-center text-green-400 text-sm">
                              <CheckCircle size={16} className="mr-1" />
                              <span>Ready</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <FileText className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-400">No documents uploaded</p>
                    </div>
                  )}
                  
                  <div className="mt-4 flex justify-between items-center text-sm">
                    <span className="text-gray-400">
                      {Object.keys(documentUploads).length} of {getEnabledDocuments().length} documents uploaded
                    </span>
                    {getRequiredDocuments().length > 0 && (
                      <span className={Object.keys(documentUploads).length >= getRequiredDocuments().length ? 'text-green-400' : 'text-red-400'}>
                        {Object.keys(documentUploads).length >= getRequiredDocuments().length ? 'All required documents uploaded' : `${getRequiredDocuments().length - Object.keys(documentUploads).length} required documents missing`}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">Additional Notes</label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Any special requests or additional information..."
                />
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-700 text-gray-300 px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-3 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Booking
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        {/* Trust Badges */}
        <div className="mt-8 bg-gray-800 rounded-2xl shadow-2xl p-6 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-3">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-medium text-white">Secure Booking</h3>
              <p className="text-sm text-gray-400">Your information is protected</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-3">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-medium text-white">Flexible Assignment</h3>
              <p className="text-sm text-gray-400">We assign you the best available bike</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mb-3">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-medium text-white">Quality Service</h3>
              <p className="text-sm text-gray-400">
                {companyBranding.displayName}
                {getEnabledAddons().length > 0 && ' with gear & equipment'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReservationForm;